import isNode from 'detect-node';
import queryString from 'query-string';

import BaseRequest from '../../../baseRequest';
import history from '../../../browser_history';
import {setUserContext, clearUserContext} from '../../../logger';

import {Credentials, User} from './userTypes';

let cachedAuthenticationToken = '';

function getAuthenticationToken(): string {
  if (!cachedAuthenticationToken) {
    try {
      cachedAuthenticationToken = JSON.parse(
        localStorage.getItem('token') || '',
      ).token;
    } catch {
      cachedAuthenticationToken = '';
    }
  }

  return cachedAuthenticationToken;
}

function setAuthenticationToken(token: string): void {
  if (!isNode) {
    localStorage.setItem('token', JSON.stringify({token}));
  }
  cachedAuthenticationToken = token;
}

function removeAuthenticationToken(): void {
  if (!isNode) {
    localStorage.removeItem('token');
  }
  cachedAuthenticationToken = '';
}

function getAuthenticationTokenFromUrl(): string {
  const token = queryString.parse(location.search).token;

  return typeof token === 'string' ? `Token ${token}` : '';
}

function removeAuthenticationTokenFromUrl(): void {
  const query = queryString.parse(location.search);

  if (query.token) {
    delete query.token;

    history?.replace({
      pathname: location.pathname,
      search: queryString.stringify(query),
    });
  }
}

function registerUser(credentials: Credentials): void {
  setUserContext({
    id: credentials.user.hash,
    email: credentials.user.email,
    organization: credentials.organization.name,
  });
}

function unregisterUser(): void {
  clearUserContext();

  document.cookie = `acceptTracking=false;max-age=0;path=/`;

  if ('gtag' in window) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const typedWindow = window as any;
    typedWindow[`ga-disable-${typedWindow.appConfig.gaTrackingId}`] = true;
  }
}

type AuthenticateResponse = {
  token: string;
  user: User;
};

function signup(
  name: string,
  email: string,
  password1: string,
  password2: string,
): Promise<AuthenticateResponse> {
  const requester = new BaseRequest<AuthenticateResponse>('register');

  return requester.post({name, email, password1, password2});
}

function authenticate(
  email: string,
  password: string,
): Promise<AuthenticateResponse> {
  const requester = new BaseRequest<AuthenticateResponse>('login');

  return requester.post({email, password});
}

function autoAuthenticate(token: string): Promise<AuthenticateResponse> {
  const requester = new BaseRequest<AuthenticateResponse>('me', token);

  return requester.getAll();
}

export {
  getAuthenticationToken,
  setAuthenticationToken,
  removeAuthenticationToken,
  getAuthenticationTokenFromUrl,
  removeAuthenticationTokenFromUrl,
  registerUser,
  unregisterUser,
  signup,
  authenticate,
  autoAuthenticate,
};
